import React, { useRef, useEffect, useState } from 'react';
import { Bold, Italic, List, ListOrdered, Underline } from 'lucide-react';
import { cn } from "@/lib/utils";

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (e: any) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  name?: string;
}

export const SimpleRichTextEditor: React.FC<SimpleRichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  className,
  editorClassName,
  name
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) {
      const fakeEvent = {
        target: {
          name: name,
          value: editorRef.current?.innerHTML || ''
        }
      };
      onBlur(fakeEvent);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  };

  const executeCommand = (e: React.MouseEvent, command: string, arg?: string) => {
    e.preventDefault(); // prevent losing focus
    document.execCommand(command, false, arg);
    editorRef.current?.focus();
    handleInput();
  };

  return (
    <div className={cn("flex flex-col border rounded-md overflow-hidden transition-colors",
      isFocused ? "border-[#0C66E4] ring-1 ring-[#0C66E4]" : "border-gray-300",
      className,
      disabled ? "opacity-80 bg-gray-50" : "bg-white")}>
      <style>{`
        .rich-text-editor:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
            display: block;
        }
        .rich-text-editor ul {
          list-style-type: disc;
          padding-inline-start: 1.5em;
        }
        .rich-text-editor ol {
          list-style-type: decimal;
          padding-inline-start: 1.5em;
        }
      `}</style>
      
      {!disabled && (
        <div className="flex items-center gap-1 p-1.5 border-b border-gray-200 bg-gray-50 flex-wrap">
          <button type="button" onMouseDown={(e) => executeCommand(e, 'bold')} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors" title="Bold"><Bold size={16} /></button>
          <button type="button" onMouseDown={(e) => executeCommand(e, 'italic')} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors" title="Italic"><Italic size={16} /></button>
          <button type="button" onMouseDown={(e) => executeCommand(e, 'underline')} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors" title="Underline"><Underline size={16} /></button>
          <div className="w-px h-4 bg-gray-300 mx-1"></div>
          <button type="button" onMouseDown={(e) => executeCommand(e, 'insertUnorderedList')} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors" title="Bullet List"><List size={16} /></button>
          <button type="button" onMouseDown={(e) => executeCommand(e, 'insertOrderedList')} className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors" title="Numbered List"><ListOrdered size={16} /></button>
        </div>
      )}
      <div
        ref={editorRef}
        dir="auto"
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        className={cn(
          "rich-text-editor p-3 outline-none overflow-y-auto text-sm text-start",
          editorClassName || "h-[200px] min-h-[120px] max-h-[600px]"
        )}
        data-placeholder={placeholder}
        style={{ resize: "vertical" }}
      />
    </div>
  );
};
export default SimpleRichTextEditor;
